/**
 * useVisualDashboard.js — Estado del builder admin de Visuales.
 *
 * CRUD optimista sobre visualsBuilderService (espejo de useDashboard.js de
 * Oportunidades Comerciales, pero sin generación por IA: la definición la
 * escribe analítica — render_config + data_sql).
 *
 * Las mutaciones leen el estado más reciente vía dashboardRef (no closures
 * capturadas), y todas reportan errores a `error` (incluido add/edit).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getDefaultVisualDashboard,
  createVisualWidget,
  updateVisualWidget,
  deleteVisualWidget,
  reorderVisualWidgets,
} from '../services/visualsBuilderService';

export function useVisualDashboard(username) {
  const [dashboard, setDashboard] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Ref siempre apuntando al dashboard más reciente: las mutaciones lo leen de
  // aquí para evitar estado obsoleto capturado en closures (reorder/remove).
  const dashboardRef = useRef(null);
  useEffect(() => { dashboardRef.current = dashboard; }, [dashboard]);

  const load = useCallback(async () => {
    if (!username) return;
    setStatus('loading'); setError(null);
    try {
      const res = await getDefaultVisualDashboard(username);
      if (mounted.current) { setDashboard(res.dashboard); setStatus('ready'); }
    } catch (e) {
      if (mounted.current) { setError(e.message); setStatus('error'); }
    }
  }, [username]);
  useEffect(() => { load(); }, [load]);

  const widgets = useMemo(() => dashboard?.widgets || [], [dashboard]);

  const addWidget = useCallback(async (payload) => {
    const cur = dashboardRef.current;
    if (!cur) return null;
    try {
      // Sin `position`: el backend la asigna atómicamente (MAX(position)+1),
      // evitando colisiones si se crean widgets en rápida sucesión.
      const res = await createVisualWidget(cur.id, { username, size: 12, ...payload });
      if (mounted.current) setDashboard((d) => (d ? { ...d, widgets: [...d.widgets, res.widget] } : d));
      return res.widget;
    } catch (e) {
      if (mounted.current) setError(e.message);
      throw e;
    }
  }, [username]);

  const editWidget = useCallback(async (widgetId, payload) => {
    const cur = dashboardRef.current;
    if (!cur) return null;
    try {
      const res = await updateVisualWidget(cur.id, widgetId, { username, ...payload });
      if (mounted.current) {
        setDashboard((d) => (d ? { ...d, widgets: d.widgets.map((w) => (w.id === widgetId ? res.widget : w)) } : d));
      }
      return res.widget;
    } catch (e) {
      if (mounted.current) setError(e.message);
      throw e;
    }
  }, [username]);

  const removeWidget = useCallback(async (widgetId) => {           // OPTIMISTA
    const prev = dashboardRef.current;
    if (!prev) return;
    setDashboard((d) => (d ? { ...d, widgets: d.widgets.filter((w) => w.id !== widgetId) } : d));
    try { await deleteVisualWidget(prev.id, widgetId, username); }
    catch (e) { if (mounted.current) { setDashboard(prev); setError(e.message); } }
  }, [username]);

  const reorder = useCallback(async (orderedIds) => {             // OPTIMISTA
    const prev = dashboardRef.current;
    if (!prev) return;
    const byId = Object.fromEntries(prev.widgets.map((w) => [w.id, w]));
    const next = orderedIds.map((id, i) => ({ ...byId[id], position: i }));
    setDashboard((d) => (d ? { ...d, widgets: next } : d));
    try { await reorderVisualWidgets(prev.id, username, orderedIds); }
    catch (e) { if (mounted.current) { setDashboard(prev); setError(e.message); } }
  }, [username]);

  const moveWidget = useCallback((index, delta) => {
    const cur = dashboardRef.current;
    if (!cur) return undefined;
    const ordered = [...cur.widgets].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const target = index + delta;
    if (target < 0 || target >= ordered.length) return undefined;
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    return reorder(ordered.map((w) => w.id));
  }, [reorder]);

  return {
    dashboard, widgets, status, error,
    isReady: status === 'ready',
    load, addWidget, editWidget, removeWidget, reorder, moveWidget,
    clearError: () => setError(null),
  };
}

export default useVisualDashboard;
